import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Crown, Zap } from 'lucide-react';

/**
 * Pure presentational card for the subscription / billing summary.
 *
 * Data comes from `GET /api/payments/subscription`. Cancel is triggered by
 * the parent (owns the confirmation dialog); this card just surfaces the
 * button.
 */
export const ProfileSubscriptionCard = ({ subscription, onExtend, onCancel }) => {
  if (!subscription) return null;

  const badgeLabel = (() => {
    if (!subscription.isPro) return 'FREE';
    return subscription.isTrial ? 'PRO (TRIAL)' : 'PRO';
  })();

  return (
    <Card
      className="bg-[#1A1A1A] border-gray-800 mb-6"
      data-testid="profile-subscription-card"
    >
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2">
            <Crown
              className="w-5 h-5"
              style={{ color: subscription.isPro ? '#00FF00' : '#888' }}
            />
            Subscription
          </CardTitle>
          <Badge
            className={
              subscription.isPro
                ? 'bg-[#00FF00]/15 text-[#00FF00] border-[#00FF00]/40'
                : 'bg-gray-800 text-gray-400 border-gray-700'
            }
          >
            {badgeLabel}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {subscription.isPro ? (
          <>
            <p className="text-gray-300 text-sm">
              {subscription.isTrial
                ? 'You are on the 14-day Pro trial.'
                : 'Pro access is active.'}
            </p>
            {subscription.proExpiresAt && (
              <p className="text-sm text-gray-400" data-testid="subscription-expires-at">
                {subscription.isTrial ? 'Trial ends' : 'Renews / expires'} on{' '}
                <span className="text-white">
                  {new Date(subscription.proExpiresAt).toLocaleDateString()}
                </span>
                {subscription.daysRemaining !== null && (
                  <span className="text-gray-500">
                    {' '}
                    ({subscription.daysRemaining} days left)
                  </span>
                )}
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <Button
                data-testid="extend-pro-btn"
                onClick={onExtend}
                className="bg-[#00FF00] text-black hover:bg-[#00DD00] font-semibold"
              >
                <Zap className="w-4 h-4 mr-2" />
                Extend by 30 days — $15
              </Button>
              <Button
                data-testid="cancel-pro-btn"
                variant="outline"
                onClick={onCancel}
                className="border-gray-700 text-gray-300 hover:bg-gray-800"
              >
                Cancel Pro
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-gray-300 text-sm">
              You are on the Free plan. Unlock unlimited rooms, interactive terminal, and full AI
              threat analysis with Pro.
            </p>
            <Button
              data-testid="profile-upgrade-btn"
              onClick={onExtend}
              className="bg-[#00FF00] text-black hover:bg-[#00DD00] font-semibold"
            >
              <Zap className="w-4 h-4 mr-2" />
              See Pro plans
            </Button>
          </>
        )}
        {subscription.transactions && subscription.transactions.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-800">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">
              Recent transactions
            </p>
            <ul className="space-y-1 text-sm" data-testid="subscription-history">
              {subscription.transactions.slice(0, 5).map((t) => (
                <li key={t.session_id} className="flex justify-between text-gray-400">
                  <span>
                    {t.package_id} · ${t.amount?.toFixed?.(2) ?? t.amount}
                  </span>
                  <span
                    className={
                      t.payment_status === 'paid' ? 'text-[#00FF00]' : 'text-gray-500'
                    }
                  >
                    {t.payment_status}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
